<script setup>
import { ref, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'

const props = defineProps({
  tabs: {
    type: Array,
    default: () => [],
  },
})

const router = useRouter()
const route = useRoute()

const emit = defineEmits(['input:updateModel', 'selectTab'])

const model = defineModel({ type: Number })

const selectedIndex = ref(0)
const tabsContainer = ref(null)
const showLeftArrow = ref(false)
const showRightArrow = ref(false)

const selectTab = i => {
  if (route.path === props.tabs[i].value) return
  selectedIndex.value = i
  router.push(props.tabs[i].value)
}

const checkScroll = () => {
  if (!tabsContainer.value) return
  const { scrollLeft, scrollWidth, clientWidth } = tabsContainer.value
  showLeftArrow.value = scrollLeft > 0
  showRightArrow.value = scrollLeft < scrollWidth - clientWidth - 5
}

const scrollLeft = () => {
  if (!tabsContainer.value) return
  tabsContainer.value.scrollBy({ left: -200, behavior: 'smooth' })
}

const scrollRight = () => {
  if (!tabsContainer.value) return
  tabsContainer.value.scrollBy({ left: 200, behavior: 'smooth' })
}

onMounted(() => {
  if (model.value) {
    selectedIndex.value = model.value
  }
  checkScroll()
  window.addEventListener('resize', checkScroll)
})

watch(selectedIndex, newVal => {
  emit('update:modelValue', newVal)
})

watch(
  () => model.value,
  newVal => {
    selectTab(newVal)
  },
)
</script>

<template>
  <div class="w-full">
    <!-- Header -->
    <div class="relative flex items-center">
      <!-- Left Arrow -->
      <button
        v-show="showLeftArrow"
        @click="scrollLeft"
        class="flex h-full items-center bg-white px-2 md:hidden"
      >
        <ImoIcon name="arrow-left-02" :size="16" />
      </button>

      <!-- Tabs Container -->
      <div
        ref="tabsContainer"
        @scroll="checkScroll"
        class="border-neutral-40 flex flex-row overflow-x-auto overflow-y-hidden border-b md:justify-start"
        style="scroll-behavior: smooth; scrollbar-width: none; -ms-overflow-style: none"
      >
        <div
          v-for="(tab, index) in tabs"
          :key="tab.title"
          @click="selectTab(index)"
          class="flex shrink-0 cursor-pointer flex-row"
        >
          <button
            class="text-neutral-60 flex items-center p-2 text-sm font-medium"
            :class="{
              'border-secondary-50 bg-error-10 text-secondary-50 mb-[-1px] border-b-2 font-semibold':
                $route.path === tab.value,
              'hover:bg-neutral-20 hover:text-neutral-90': selectedIndex !== index,
            }"
          >
            <ImoIcon v-if="tab.icon" :name="tab.icon" class="mr-1" :size="20" />
            {{ tab.label }}
          </button>
        </div>
      </div>

      <!-- Right Arrow -->
      <button
        v-show="showRightArrow"
        @click="scrollRight"
        class="flex h-full items-center bg-white px-2 md:hidden"
      >
        <ImoIcon name="arrow-right-02" :size="16" />
      </button>
    </div>
  </div>
</template>

<style scoped>
/* Hide scrollbar for Chrome, Safari and Opera */
div[ref='tabsContainer']::-webkit-scrollbar {
  display: none;
}
</style>
